import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Pane,
  PaneHeader,
  PaneMenu,
  IconButton,
  Button,
  Row,
  Col,
  Card,
  Loading,
  Callout,
  Select,
  TextField,
  Checkbox,
  MultiColumnList,
} from '@folio/stripes/components';
import { useStripes } from '@folio/stripes/core';

import BarcodePreview from './BarcodePreview';
import ProfessionalBarcodePreview from './ProfessionalBarcodePreview';
import UserFriendlyPreview from './UserFriendlyPreview';
import PrintSettings from './PrintSettings';
import PrintHelper from './PrintHelper';
import ProfessionalPrinterHelper from './ProfessionalPrinterHelper';
import PrinterDetection from './PrinterDetection';
import EnhancedBarcodeInterface from './EnhancedBarcodeInterface';
import FileBasedPrinting from './FileBasedPrinting'; // kware: إضافة خدمة طباعة الملفات
import LocalStorageManager from './LocalStorageManager'; // kware: إضافة مدير التخزين المحلي
import useInstance from '../../../hooks/useInstance';
import useHoldings from '../../../hooks/useHoldings';
import useItems from '../../../hooks/useItems';

// kware: إضافة React hooks المطلوبة للتأثيرات الجانبية

const BarcodePrintingPage = ({ basePath }) => {
  const { instanceId } = useParams();
  console.log({ instanceId });
  const history = useHistory();
  const intl = useIntl();
  const stripes = useStripes();
  const callout = useRef();

  const [selectedItems, setSelectedItems] = useState([]);
  const [printSettings, setPrintSettings] = useState(() => {
    // kware: تحميل الإعدادات المحفوظة من LocalStorage
    return storageManager.current?.getSettings() || {
      format: 'CODE128',
      layout: 'standard',
      includeText: true,
      copies: 1,
      paperSize: 'A4',
      orientation: 'portrait',
      printerMode: 'browser',
      printerType: 'ZEBRA_ZPL',
      templateId: 'zebra-standard', // kware: إضافة قالب افتراضي
      exportFormat: 'zpl' // kware: إضافة تنسيق تصدير افتراضي
    };
  });
  const [isPrinting, setIsPrinting] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState(() => {
    // kware: تحميل آخر طابعة مستخدمة من LocalStorage
    return storageManager.current?.getLastUsedPrinter() || null;
  });
  const [discoveredPrinters, setDiscoveredPrinters] = useState([]); // kware: حالة الطابعات المكتشفة
  const [showPrinterDetection, setShowPrinterDetection] = useState(false);
  const [previewType, setPreviewType] = useState('user'); // user, professional, basic
  const printHelper = useRef(new PrintHelper());
  const professionalPrinter = useRef(new ProfessionalPrinterHelper());
  const fileBasedPrinter = useRef(new FileBasedPrinting()); // kware: إضافة خدمة طباعة الملفات
  const storageManager = useRef(new LocalStorageManager()); // kware: إضافة مدير التخزين المحلي

  // Use existing hooks to fetch instance, holdings, and items data
  const {
    instance,
    isInstanceLoading,
    isError: instanceError,
  } = useInstance(instanceId);
  const {
    holdings,
    isHoldingsLoading,
    isError: holdingsError,
  } = useHoldings(instanceId);
  const { items: holdingsItems, isItemsLoading } = useItems(holdings);

  // Flatten items from all holdings and filter only items with barcodes
  const items = Object.values(holdingsItems || {})
    .flat()
    .filter((item) => item.barcode);

  const isLoading = isInstanceLoading || isHoldingsLoading || isItemsLoading;
  const error = instanceError || holdingsError;

  // kware: تأثير جانبي لحفظ الإعدادات عند التغيير
  useEffect(() => {
    if (printSettings && Object.keys(printSettings).length > 0) {
      const saveTimer = setTimeout(() => {
        storageManager.current.saveSettings(printSettings);
      }, 1000); // حفظ بعد ثانية واحدة من آخر تغيير

      return () => clearTimeout(saveTimer);
    }
  }, [printSettings]);

  // kware: تأثير جانبي لتحميل الطابعات المحفوظة عند بدء التشغيل
  useEffect(() => {
    const loadSavedPrinters = () => {
      const savedPrinters = storageManager.current.getAllPrinters();
      if (savedPrinters && Object.keys(savedPrinters).length > 0) {
        const printersArray = Object.values(savedPrinters);
        setDiscoveredPrinters(printersArray);
      }
    };

    loadSavedPrinters();
  }, []);

  const handleClose = () => {
    history.push('/inventory');
  };

  // kware: معالج تحديث قائمة الطابعات المكتشفة مع حفظ في LocalStorage
  const handlePrintersDiscovered = (printers) => {
    setDiscoveredPrinters(printers);

    // حفظ الطابعات المكتشفة في LocalStorage
    if (printers && printers.length > 0) {
      printers.forEach(printer => {
        if (printer.saved !== false) { // لا نحفظ الطابعات المؤقتة
          storageManager.current.savePrinter(printer);
        }
      });
    }
  };

  // kware: معالج اختيار الطابعة من قائمة الاكتشاف مع حفظ في LocalStorage
  const handlePrinterSelected = (printer) => {
    setSelectedPrinter(printer);

    // حفظ الطابعة وتحديث آخر استخدام
    if (printer && printer.id) {
      storageManager.current.savePrinter(printer);
      storageManager.current.updatePrinterUsage(printer.id);
    }

    // حفظ إعدادات الطابعة
    if (printer.capabilities && printer.capabilities.length > 0) {
      setPrintSettings(prev => ({
        ...prev,
        printerType: printer.capabilities[0] // استخدام أول قدرة متاحة
      }));
    }
  };

  const handleItemSelection = (item, isSelected) => {
    if (Array.isArray(item)) {
      // Batch selection (Select All/Deselect All)
      if (isSelected) {
        setSelectedItems(item);
      } else {
        setSelectedItems([]);
      }
    } else {
      // Single item selection
      if (isSelected) {
        setSelectedItems((prev) => [...prev, item]);
      } else {
        setSelectedItems((prev) => prev.filter((selected) => selected.id !== item.id));
      }
    }
  };

  const handleSelectAll = () => {
    const itemsWithBarcodes = items.filter((item) => item.barcode);
    setSelectedItems(itemsWithBarcodes);
  };

  const handleDeselectAll = () => {
    setSelectedItems([]);
  };

  const handlePrint = async () => {
    if (selectedItems.length === 0) {
      callout.current?.sendCallout({
        type: 'error',
        message: intl.formatMessage({
          id: 'ui-plugin-inventory-templates.barcodePrinting.error.noItemsSelected',
          defaultMessage: 'Please select at least one item to print',
        }),
      });
      return;
    }

    setIsPrinting(true);

    try {
      let result;

      switch (printSettings.printerMode) {
        case 'professional':
          // Professional printer mode
          if (!professionalPrinter.current.isProfessionalPrintingSupported()) {
            throw new Error(intl.formatMessage({
              id: 'ui-plugin-inventory-templates.barcodePrinting.error.professionalNotSupported',
              defaultMessage: 'Professional printing is not supported in this browser'
            }));
          }
          result = await professionalPrinter.current.executeProfessionalPrint(selectedItems, printSettings);
          break;

        case 'download':
          // kware: تحديث وضع التحميل لاستخدام خدمة طباعة الملفات المحسنة
          try {
            const exportResult = await fileBasedPrinter.current.exportForNetworkPrinting(
              selectedItems,
              printSettings
            );

            // kware: حفظ تاريخ التحميل في LocalStorage
            storageManager.current.saveDownloadHistory(exportResult);

            // Create and trigger download
            const link = document.createElement('a');
            link.href = exportResult.downloadUrl;
            link.download = exportResult.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Show instructions in callout
            callout.current?.sendCallout({
              type: 'success',
              message: (
                <div>
                  <strong>تم تحميل الملف: {exportResult.filename}</strong>
                  <details style={{ marginTop: '8px' }}>
                    <summary>تعليمات الطباعة الشبكية</summary>
                    <pre style={{ fontSize: '11px', marginTop: '8px' }}>
                      {exportResult.instructions}
                    </pre>
                  </details>
                </div>
              ),
              timeout: 10000
            });

            // Cleanup URL after delay
            setTimeout(() => {
              URL.revokeObjectURL(exportResult.downloadUrl);
            }, 2000);

            result = {
              success: true,
              message: `Downloaded ${exportResult.filename} (${exportResult.metadata.totalLabels} labels)`,
              itemCount: selectedItems.length
            };
          } catch (downloadError) {
            // Fallback to old method
            const printFile = professionalPrinter.current.generatePrintFile(selectedItems, printSettings);
            const link = document.createElement('a');
            link.href = printFile.downloadUrl;
            link.download = printFile.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(printFile.downloadUrl), 1000);

            result = {
              success: true,
              message: `Downloaded ${printFile.filename} (fallback)`,
              itemCount: selectedItems.length
            };
          }
          break;

        default:
          // Browser printing mode (fallback)
          if (!printHelper.current.isPrintingSupported()) {
            throw new Error(intl.formatMessage({
              id: 'ui-plugin-inventory-templates.barcodePrinting.error.printNotSupported',
              defaultMessage: 'Printing is not supported in this browser'
            }));
          }
          result = await printHelper.current.executePrint(selectedItems, printSettings);
      }

      if (result.success) {
        callout.current?.sendCallout({
          type: 'success',
          message: intl.formatMessage(
            {
              id: 'ui-plugin-inventory-templates.barcodePrinting.success',
              defaultMessage: '{count} barcodes processed successfully',
            },
            { count: result.itemCount },
          ),
        });

        // kware: حفظ الإعدادات بعد الطباعة الناجحة
        storageManager.current.saveSettings(printSettings);

        // Reset selected items after successful operation
        setSelectedItems([]);
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      callout.current?.sendCallout({
        type: 'error',
        message: intl.formatMessage({
          id: 'ui-plugin-inventory-templates.barcodePrinting.error.printFailed',
          defaultMessage: 'Operation failed: {error}',
        }, { error: err.message || 'Unknown error' }),
      });
    } finally {
      setIsPrinting(false);
    }
  };

  const renderActionMenu = () => (
    <PaneMenu>
      <IconButton
        icon="times"
        id="close-barcode-printing"
        onClick={handleClose}
        aria-label={intl.formatMessage({
          id: 'ui-plugin-inventory-templates.close',
          defaultMessage: 'Close',
        })}
      />
    </PaneMenu>
  );

  const itemsWithBarcodes = items?.filter((item) => item.barcode) || [];
  const columnMapping = {
    barcode: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcodePrinting.columns.barcode',
      defaultMessage: 'Barcode',
    }),
    materialType: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcodePrinting.columns.materialType',
      defaultMessage: 'Material Type',
    }),
    location: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcodePrinting.columns.location',
      defaultMessage: 'Location',
    }),
    callNumber: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcodePrinting.columns.callNumber',
      defaultMessage: 'Call Number',
    }),
    selected: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcodePrinting.columns.selected',
      defaultMessage: 'Select',
    }),
  };

  const formatter = {
    barcode: (item) => item.barcode,
    materialType: (item) => item.materialType?.name || '',
    location: (item) => item.effectiveLocation?.name || '',
    callNumber: (item) => item.effectiveCallNumberComponents?.callNumber || '',
    selected: (item) => (
      <Checkbox
        checked={selectedItems.some((selected) => selected.id === item.id)}
        onChange={(e) => handleItemSelection(item, e.target.checked)}
      />
    ),
  };

  if (isLoading) {
    return (
      <Pane
        defaultWidth="100%"
        paneTitle={intl.formatMessage({
          id: 'ui-plugin-inventory-templates.barcodePrinting.title',
          defaultMessage: 'Print Barcodes',
        })}
        lastMenu={renderActionMenu()}
      >
        <Loading />
      </Pane>
    );
  }

  if (error) {
    return (
      <Pane
        defaultWidth="100%"
        paneTitle={intl.formatMessage({
          id: 'ui-plugin-inventory-templates.barcodePrinting.title',
          defaultMessage: 'Print Barcodes',
        })}
        lastMenu={renderActionMenu()}
      >
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.barcodePrinting.error.loadFailed"
            defaultMessage="Failed to load items for this instance"
          />
        </div>
      </Pane>
    );
  }

  return (
    <EnhancedBarcodeInterface
      items={items}
      instance={instance}
      selectedItems={selectedItems}
      onItemSelection={handleItemSelection}
      printSettings={printSettings}
      onSettingsChange={setPrintSettings}
      onPrint={handlePrint}
      isPrinting={isPrinting}
      onClose={handleClose}
      selectedPrinter={selectedPrinter}
      onPrinterSelected={handlePrinterSelected}
      discoveredPrinters={discoveredPrinters} // kware: تمرير الطابعات المكتشفة
      onPrintersDiscovered={handlePrintersDiscovered} // kware: معالج اكتشاف الطابعات
    />
  );
};

BarcodePrintingPage.propTypes = {
  basePath: PropTypes.string.isRequired,
};

export default BarcodePrintingPage;
